import { on } from '@ngrx/store';
import { FormsConstants } from '../../../types/forms-constants';
import {
    collapseAccordionByGroup,
    collapseAccordionWithChildren,
    expandAccordionByGroup,
    expandAccordionWithChildren,
    registerAccordionGroups,
} from '../../actions/form.actions';
import { FormState } from '../../state/form.state';
import { wildcardToRegExp } from './form-reducer.utilities';

export function registerAccordionGroupsReducer(
    state: Readonly<FormState>,
    props: {
        groupIds: string[];
    },
): FormState {
    const accordionGroups: Record<string, boolean> = {};
    props.groupIds.forEach((value) => {
        accordionGroups[value] = false;
    });
    return { ...state, accordionGroups };
}

export function expandAccordionByGroupReducer(
    state: Readonly<FormState>,
    props: {
        groupId: string;
    },
): FormState {
    const accordionGroup: Record<string, boolean> = { ...state.accordionGroups };
    const regExp = wildcardToRegExp(props.groupId);

    Object.keys(accordionGroup)
        .filter((key) => key.match(regExp))
        .forEach((filteredKey) => (accordionGroup[filteredKey] = true));

    return {
        ...state,
        accordionGroups: { ...accordionGroup },
    };
}

export function collapseAccordionByGroupReducer(
    state: Readonly<FormState>,
    props: {
        groupId: string;
    },
): FormState {
    const accordionGroup: Record<string, boolean> = { ...state.accordionGroups };
    const regExp = wildcardToRegExp(props.groupId);

    Object.keys(accordionGroup)
        .filter((key) => key.match(regExp))
        .forEach((filteredKey) => (accordionGroup[filteredKey] = false));

    return {
        ...state,
        accordionGroups: { ...accordionGroup },
    };
}

function getAccordionChildren(state: Readonly<FormState>, groupId: number): string[] {
    const headingsArray = Object.values(state.headings);
    const currentHeading = state.headings[groupId];

    const position = headingsArray.findIndex((heading) => heading.id === groupId);
    const remainingHeadings = headingsArray.splice(position + 1);

    const childrenHeadings: string[] = [];
    childrenHeadings.push(`${FormsConstants.SECTION_TEXT}-${currentHeading.chapterNumber}-${currentHeading.id}`);

    for (const heading of remainingHeadings) {
        // if a new chapter starts, or the same chapter appears again,
        // do not detect the elements as children
        if (
            heading.chapterNumber === currentHeading.chapterNumber ||
            !heading.chapterNumber.startsWith(currentHeading.chapterNumber)
        ) {
            break;
        }

        childrenHeadings.push(`${FormsConstants.SECTION_TEXT}-${heading.chapterNumber}-${heading.id}`);
    }

    return childrenHeadings;
}
export function expandAccordionWithChildrenReducer(
    state: Readonly<FormState>,
    props: {
        groupId: number;
    },
): FormState {
    const accordionGroup: Record<string, boolean> = { ...state.accordionGroups };

    const childrenHeadings = getAccordionChildren(state, props.groupId);
    childrenHeadings.forEach((h) => (accordionGroup[h] = true));

    return {
        ...state,
        accordionGroups: { ...accordionGroup },
    };
}

export function collapseAccordionWithChildrenReducer(
    state: Readonly<FormState>,
    props: {
        groupId: number;
    },
): FormState {
    const accordionGroup: Record<string, boolean> = { ...state.accordionGroups };

    const childrenHeadings = getAccordionChildren(state, props.groupId);
    childrenHeadings.forEach((h) => (accordionGroup[h] = false));

    return {
        ...state,
        accordionGroups: { ...accordionGroup },
    };
}

export const accordionReducer = [
    on<FormState, Array<typeof registerAccordionGroups>>(registerAccordionGroups, registerAccordionGroupsReducer),
    on<FormState, Array<typeof expandAccordionByGroup>>(expandAccordionByGroup, expandAccordionByGroupReducer),
    on<FormState, Array<typeof collapseAccordionByGroup>>(collapseAccordionByGroup, collapseAccordionByGroupReducer),
    on<FormState, Array<typeof expandAccordionWithChildren>>(
        expandAccordionWithChildren,
        expandAccordionWithChildrenReducer,
    ),
    on<FormState, Array<typeof collapseAccordionWithChildren>>(
        collapseAccordionWithChildren,
        collapseAccordionWithChildrenReducer,
    ),
];
